import { TextField, Alert, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState, useCallback, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { useUser } from '@/utils/supabase/useUser';
import { sendGTMEvent } from '@next/third-parties/google';
import { PAGE_PATHS } from '@/constants/constants';
import { z } from 'zod';
import { useRouter, useSearchParams } from 'next/navigation';
import useHash from '@/customHooks/useHash';
import { set } from 'date-fns';

const baseSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().optional().nullable()
});

const schema = z.discriminatedUnion('loginMethod', [
  baseSchema.extend({
    loginMethod: z.literal('password'),
    password: z.string().min(6, 'Password must be at least 6 characters')
  }),
  baseSchema.extend({
    loginMethod: z.literal('link')
  })
]);

type Login = z.infer<typeof schema>;
type LoginMethod = 'link' | 'password';

export const EmailLogin = ({ next = '/' }: { next?: string }) => {
  const { supabase } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const hash = useHash();

  const redirectParam = searchParams.get('redirect');
  const nextParam = searchParams.get('next');

  const redirectURL = redirectParam
    ? `${redirectParam}${hash ? `#${hash}` : ''}`
    : null;
  const nextPath = nextParam ? `${nextParam}${hash ? `#${hash}` : ''}` : null;

  const loginMethodParam = searchParams.get('loginMethod');
  const initialLoginMethod =
    loginMethodParam === 'password' || loginMethodParam === 'link'
      ? loginMethodParam
      : 'link';

  const {
    formState: { isValid, errors, isSubmitting, isSubmitSuccessful },
    getValues,
    setValue,
    register,
    setError,
    clearErrors,
    handleSubmit
  } = useForm<Login>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      loginMethod: initialLoginMethod
    }
  });

  const loginMethod = getValues('loginMethod');

  const buildLoginMethodUrl = (newMethod: LoginMethod) => {
    const params = new URLSearchParams(window.location.search);
    params.set('loginMethod', newMethod);
    return `${window.location.pathname}?${params.toString()}`;
  };

  useEffect(() => {
    router.replace(buildLoginMethodUrl(loginMethod));
  }, [loginMethod, router]);

  const switchLoginMethod = useCallback(() => {
    const newMethod = loginMethod === 'link' ? 'password' : 'link';
    setValue('loginMethod', newMethod, { shouldValidate: true });
  }, [loginMethod, setValue]);

  const signIn = async (values: Login) => {
    clearErrors();
    if (values.loginMethod === 'password') {
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password
      });

      if (error) {
        setError('root', { type: 'error', message: error.message });
        return;
      }

      sendGTMEvent({
        event: 'sign_in',
        method: 'email'
      });

      router.replace(`${redirectURL ?? nextPath ?? '/'}`);
    } else {
      const redirectTo = new URL(PAGE_PATHS.authVerify, window.location.origin);
      redirectTo.searchParams.set('next', next);

      const { error } = await supabase.auth.signInWithOtp({
        email: values.email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: redirectTo.toString()
        }
      });

      setError('root', {
        type: 'info',
        message: `If an account exists for ${values.email}, you will receive a Login link shortly.`
      });

      if (!error) {
        sendGTMEvent({
          event: 'sign_in',
          method: 'magic_link_request'
        });
      }
    }
  };

  function onSubmit(values: Login) {
    if (isValid && !isSubmitting) {
      void signIn(values);
    }
  }

  return (
    <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
      <TextField
        margin='normal'
        required
        fullWidth
        id='email'
        label='Email Address'
        autoComplete='email'
        {...register('email')}
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isSubmitting || isSubmitSuccessful}
        autoFocus
      />
      {loginMethod === 'password' && (
        <TextField
          margin='normal'
          required
          fullWidth
          id='password'
          label='Password'
          type='password'
          autoComplete='current-password'
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
          inputProps={{ maxLength: 72 }}
          disabled={isSubmitting || isSubmitSuccessful}
        />
      )}

      {errors.root && (
        <Alert severity='success' icon={false}>
          {errors.root.message}
        </Alert>
      )}

      <LoadingButton
        type='submit'
        variant='contained'
        disableElevation
        color='primary'
        disabled={!isValid || isSubmitSuccessful}
        loading={isSubmitting}
      >
        {loginMethod === 'link' ? 'SEND LOGIN LINK' : 'SIGN IN'}
      </LoadingButton>

      <Alert
        variant='outlined'
        severity='info'
        icon={false}
        sx={{
          border: '1px solid #C5C5C5',
          background: '#FAFAFA'
        }}
      >
        <Typography variant='body1' textAlign={'start'}>
          {loginMethod === 'link' ? (
            <>
              We’ll email you a login link for a password-free sign in. Or you
              can{' '}
            </>
          ) : (
            <>Forgot password? </>
          )}
          <span
            onMouseDown={(e) => e.preventDefault()}
            onClick={switchLoginMethod}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            {loginMethod === 'link'
              ? 'sign in with password instead.'
              : 'Return to Send Login Link'}
          </span>
        </Typography>
      </Alert>
    </Stack>
  );
};
